import React from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Avatar,
  Paper,
  TableContainer,
} from "@mui/material";
import {
  Edit as EditIcon,
  Block as BlockIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { Employee } from "../../../validation/business.validation";
import ConfirmDeleteDialog from "../../../common/confirmDeleteAlert";
import ConfirmBlockDialog from "../../../common/confirmBlockAlert";
import { formatPhoneNumber } from "../../../helper/phoneFormatter.helper";

interface Props {
  employees: Employee[];
  onDelete: (emp: Employee) => void;
  onEdit: (emp: Employee) => void;
  onBlock: (emp: Employee) => void;
}

const EmployeeTableView: React.FC<Props> = ({ employees, onDelete, onEdit, onBlock }) => {
  const [openDialog, setOpenDialog] = React.useState(false);
  const [openStatusDialog, setOpenStatusDialog] = React.useState(false);
  const [selectedEmployee, setSelectedEmployee] = React.useState<Employee | null>(null);

  const handleOpenDialog = (emp: Employee) => {
    setSelectedEmployee(emp);
    setOpenDialog(true);
  };

  const handleDelete = () => {
    if (selectedEmployee) {
      onDelete(selectedEmployee);
    }
    setOpenDialog(false);
    setSelectedEmployee(null);
  };

  const handleEdit = (emp: Employee) => {
    onEdit(emp);
  };

  const openDeactivateDialog = (emp: Employee) => {
    setSelectedEmployee(emp);
    setOpenStatusDialog(true);
  };

  const handleStatusConfirm = () => {
    if (selectedEmployee) {
      onBlock(selectedEmployee);
    }
    setOpenStatusDialog(false);
    setSelectedEmployee(null);
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ borderRadius: 3}}>
        <Table sx={{ borderCollapse: "collapse" }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f9fafb" }}>
              <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
              <TableCell sx={{ fontWeight: "bold", border: "1px solid #e5e7eb" }}>Email</TableCell>
              <TableCell sx={{ fontWeight: "bold", border: "1px solid #e5e7eb" }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: "bold", border: "1px solid #e5e7eb", textAlign: "center" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.map((emp, index) => (
              <TableRow
                key={index}
                hover
                className={emp.isActive === false ? "bg-gray-100" : ""}
              >
                <TableCell
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Avatar sx={{ bgcolor: "#f3f4f6", color: "#6b7280", width: 32, height: 32 }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                  {`${(emp as any).firstName || ''} ${(emp as any).middleName || ''} ${(emp as any).lastName || ''}`.trim() || (emp as any).name || "-"}
                </TableCell>
                <TableCell sx={{ border: "1px solid #e5e7eb" }}>{emp.email || "-"}</TableCell>
                <TableCell sx={{ border: "1px solid #e5e7eb" }}>{emp.phone ? formatPhoneNumber(emp.phone) : "-"}</TableCell>
                <TableCell sx={{ border: "1px solid #e5e7eb", textAlign: "center", whiteSpace: "nowrap" }}>
                  <div style={{ display: "flex", justifyContent: "center", gap: 4 }}>
                    <Tooltip title="Edit">
                      <IconButton color="secondary" size="small" onClick={() => handleEdit(emp)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deactivate">
                      <IconButton size="small" sx={{ color: "#f59e0b" }} onClick={() => openDeactivateDialog(emp)}>
                        <BlockIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton color="error" size="small" onClick={() => handleOpenDialog(emp)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDeleteDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onConfirm={handleDelete}
        itemName={selectedEmployee?.name || ""}
      />

      <ConfirmBlockDialog
        open={openStatusDialog}
        isActive={!!selectedEmployee?.isActive}
        onClose={() => setOpenStatusDialog(false)}
        onConfirm={handleStatusConfirm}
        itemName={selectedEmployee?.name}
      />
    </>
  );
};

export default EmployeeTableView;
